import type {
  GraphCanonicalEntity,
  GraphEdge,
  GraphEntityType,
  GraphNode,
  GraphProjectionResponse,
  GraphVisualizationFrame
} from "@salesko/contracts";
import { entityMeta } from "../entityMeta";

const overviewFrameId = "tenant-overview";
const filterOrder: GraphEntityType[] = ["person", "organisation", "event", "opportunity", "note"];

export function overviewNodeIdFromCanonicalEntityId(canonicalEntityId: string) {
  return `overview:${canonicalEntityId}`;
}

export function overviewNodeId(entity: GraphCanonicalEntity) {
  return overviewNodeIdFromCanonicalEntityId(entity.id);
}

export function overviewEntityIdFromNodeId(nodeId: string) {
  return nodeId.startsWith("overview:") ? nodeId.slice("overview:".length) : null;
}

export function overviewFrameFromCanonicalEntities(entities: GraphCanonicalEntity[]): GraphVisualizationFrame {
  const nodes = entities.map((entity, index) => overviewNodeFromCanonicalEntity(entity, index, entities.length));

  return overviewFrameFromGraph(nodes, []);
}

export function overviewFrameFromProjection(projection: GraphProjectionResponse | null | undefined): GraphVisualizationFrame {
  const readNodes = (projection?.nodes ?? []).filter(hasCanonicalEntityId);
  const nodes = readNodes.map((node, index) => overviewNodeFromProjectionNode(node, index, readNodes.length));
  const visibleNodeIds = new Set(nodes.map((node) => node.id));
  const edges = (projection?.edges ?? [])
    .filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target))
    .map(overviewEdgeFromProjectionEdge);

  return overviewFrameFromGraph(nodes, edges);
}

function overviewFrameFromGraph(nodes: GraphNode[], edges: GraphEdge[]): GraphVisualizationFrame {
  const stats = statsFromNodes(nodes);

  return {
    schemaVersion: "graph.visualization.v1",
    frameId: overviewFrameId,
    graph: {
      nodes,
      edges
    },
    timeline: [],
    camera: {
      mode: "flat",
      position: { x: 0, y: 0, z: 9 },
      orbit: { radius: 9, autoRotate: false }
    },
    ui: {
      productName: "Salesko",
      activeView: "overview",
      searchPlaceholder: "Search people, companies, events...",
      filters: filterOrder.map((entityType) => ({
        entityType,
        enabled: true,
        color: entityMeta[entityType].color,
        count: countForEntityType(stats, entityType)
      })),
      relationshipStrength: 0.58,
      detailPanel: {
        ...(nodes[0] ? { selectedNodeId: nodes[0].id } : {}),
        title: "Relationship overview",
        subtitle: `${nodes.length} canonical nodes / ${edges.length} relationships`,
        tags: ["Overview", "Canonical"],
        about: "Canonical entity overview across saved graph frames.",
        linkedNodeIds: nodes[0] ? linkedNodeIdsForNode(nodes[0].id, edges) : []
      },
      stats
    },
    commands: [],
    facets: {
      entityTypes: uniqueNodeEntityTypes(nodes),
      relationTypes: uniqueRelationTypes(edges),
      warnings: []
    },
    summary: `Relationship overview: ${nodes.length} canonical nodes and ${edges.length} relationships across saved frames.`,
    needsReview: false,
    source: "deterministic-rule"
  };
}

function overviewNodeFromCanonicalEntity(
  entity: GraphCanonicalEntity,
  index: number,
  total: number
): GraphNode {
  const point = radialPoint(index, total);
  return {
    id: overviewNodeId(entity),
    type: entity.entityType,
    label: entity.label,
    ...(entity.summary ? { summary: entity.summary } : {}),
    confidence: entity.confidence,
    visual: {
      position: point,
      color: entityMeta[entity.entityType].color,
      size: Math.max(0.9, Math.min(2.4, 0.9 + entity.frameRecordCount * 0.18 + entity.nodeCount * 0.08)),
      emphasis: "normal"
    },
    data: {
      ...entity.data,
      canonicalEntityId: entity.id,
      frameRecordCount: entity.frameRecordCount,
      latestFrameRecordId: entity.latestFrameRecordId,
      latestNodeId: entity.latestNodeId,
      lastSeenAt: entity.lastSeenAt,
      nodeCount: entity.nodeCount
    }
  };
}

function overviewNodeFromProjectionNode(
  node: GraphProjectionResponse["nodes"][number] & { canonicalEntityId: string },
  index: number,
  total: number
): GraphNode {
  const point = radialPoint(index, total);
  const frameRecordCount = numberDataValue(node.data.frameRecordCount, 1);
  const nodeCount = numberDataValue(node.data.nodeCount, 1);
  return {
    id: node.id,
    type: node.entityType,
    label: node.label,
    ...(node.summary ? { summary: node.summary } : {}),
    confidence: node.confidence,
    ...(node.evidence ? { evidence: node.evidence } : {}),
    ...(node.occurredAt ? { occurredAt: node.occurredAt } : {}),
    visual: {
      position: point,
      color: entityMeta[node.entityType].color,
      size: Math.max(0.9, Math.min(2.4, 0.9 + frameRecordCount * 0.18 + nodeCount * 0.08)),
      emphasis: "normal"
    },
    data: {
      ...node.data,
      canonicalEntityId: node.canonicalEntityId
    }
  };
}

function overviewEdgeFromProjectionEdge(edge: GraphProjectionResponse["edges"][number]): GraphEdge {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    type: edge.relationType,
    label: edge.label,
    confidence: edge.confidence,
    ...(edge.evidence ? { evidence: edge.evidence } : {}),
    strength: edge.strength,
    data: edge.data
  };
}

function radialPoint(index: number, total: number) {
  if (total <= 1) {
    return { x: 0, y: 0, z: 0 };
  }

  const ring = Math.floor(Math.sqrt(index));
  const start = ring * ring;
  const count = Math.max(1, (ring + 1) * (ring + 1) - start);
  const positionInRing = index - start;
  const angle = (Math.PI * 2 * positionInRing) / count - Math.PI / 2;
  const radius = 1.35 + ring * 1.05;

  return {
    x: Number((Math.cos(angle) * radius).toFixed(3)),
    y: Number((Math.sin(angle) * radius).toFixed(3)),
    z: Number((((index % 5) - 2) * 0.16).toFixed(3))
  };
}

function hasCanonicalEntityId(
  node: GraphProjectionResponse["nodes"][number]
): node is GraphProjectionResponse["nodes"][number] & { canonicalEntityId: string } {
  return typeof node.canonicalEntityId === "string" && node.canonicalEntityId.length > 0;
}

function numberDataValue(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function statsFromNodes(nodes: GraphNode[]): GraphVisualizationFrame["ui"]["stats"] {
  return {
    people: nodes.filter((node) => node.type === "person").length,
    events: nodes.filter((node) => node.type === "event").length,
    organisations: nodes.filter((node) => node.type === "organisation").length,
    opportunities: nodes.filter((node) => node.type === "opportunity").length
  };
}

function uniqueNodeEntityTypes(nodes: GraphNode[]) {
  return filterOrder.filter((entityType) => nodes.some((node) => node.type === entityType));
}

function uniqueRelationTypes(edges: GraphEdge[]) {
  return [...new Set(edges.map((edge) => edge.type))];
}

function linkedNodeIdsForNode(nodeId: string, edges: GraphEdge[]) {
  return [
    ...new Set(
      edges
        .filter((edge) => edge.source === nodeId || edge.target === nodeId)
        .map((edge) => (edge.source === nodeId ? edge.target : edge.source))
    )
  ];
}

function countForEntityType(
  stats: GraphVisualizationFrame["ui"]["stats"],
  entityType: GraphEntityType
) {
  switch (entityType) {
    case "person":
      return stats.people;
    case "organisation":
      return stats.organisations;
    case "event":
      return stats.events;
    case "opportunity":
      return stats.opportunities;
    case "note":
      return 0;
  }
}
